/**
 * PhysicalExamBox — the copy/paste-ready physical exam.
 *
 * Renders the exact HTML that goes on the clipboard (same generator, no second
 * code path), so the preview is a true WYSIWYG of what lands in the report.
 * Copy writes both text/html and text/plain; a browser that rejects the rich
 * ClipboardItem still gets usable plain text.
 */

import React, { useMemo, useState } from 'react';
import {
  buildPhysicalExam,
  physicalExamHtml,
  physicalExamText,
} from '../utils/physicalExam';
import { writeToClipboard } from '../utils/reportClipboard';

interface PhysicalExamBoxProps {
  examData: any;
}

const PhysicalExamBox: React.FC<PhysicalExamBoxProps> = ({ examData }) => {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { html, text, regionCount } = useMemo(() => {
    const regions = buildPhysicalExam(examData);
    return {
      html: physicalExamHtml(regions),
      text: physicalExamText(regions),
      regionCount: regions.length,
    };
  }, [examData]);

  const handleCopy = async () => {
    setError(null);
    // Same clipboard path as the Results Summary and as QME - Tool: one item
    // carrying text/html + text/plain, falling back to plain text.
    const ok = await writeToClipboard(html, text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } else {
      setError('Could not copy — select the exam below and copy manually.');
    }
  };

  if (regionCount === 0) {
    return (
      <div id="physical-exam" className="card p-4 mt-6 scroll-mt-4">
        <h3 className="text-lg font-medium mb-2">Physical Exam</h3>
        <p className="text-content-tertiary text-sm">
          Enter or parse exam values and the formatted physical exam will appear here,
          ready to copy into your report.
        </p>
      </div>
    );
  }

  return (
    <div id="physical-exam" className="card p-4 mt-6 scroll-mt-4">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-medium">Physical Exam</h3>
          <p className="text-content-tertiary text-sm">
            {regionCount} region{regionCount === 1 ? '' : 's'} · abnormal values in bold
          </p>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="btn-primary whitespace-nowrap"
          aria-label="Copy the physical exam to the clipboard"
        >
          {copied ? 'Copied' : 'Copy exam'}
        </button>
      </div>

      {error && (
        <p role="alert" className="text-sm mb-3 text-amber-600">
          {error}
        </p>
      )}

      {/* The preview IS the clipboard payload — same string, rendered. */}
      <div
        className="bg-white text-black rounded p-4 overflow-x-auto"
        data-testid="physical-exam-preview"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

export default PhysicalExamBox;
